import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Tag, Plus, Edit, Trash2, X, Check, Search, ChevronDown, ChevronRight, ChevronUp, 
  Folder, FolderOpen, Filter, Download, Upload, FileText, FileSpreadsheet, Calendar, 
  RefreshCcw, Layers, Grid, TrendingUp, Eye, CheckSquare, Square, List, LayoutList, 
  LayoutGrid, Shield, CheckCircle, AlertCircle, FileOutput
} from 'lucide-react';
import QawaemErpCrudModal from '../../products/saudi/components/QawaemErpCrudModal';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { CustomDatePicker } from '../../shared/components/custom';
import toast from 'react-hot-toast';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError } from '../../shared/utils/crudNotify';
import { useTranslation } from 'react-i18next';
import {
  SAUDI_CODE_TYPES,
  SAUDI_DEBIT_CATEGORIES,
  SAUDI_CREDIT_CATEGORIES,
} from './saudiTreasuryDefaults';
import '../../shared/styles/GPSTrackingDatePicker.css';
import '../../core/styles/global-dropdowns.css';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import { buildCaisseModalFormTabs, CAISSE_FORM_GRID, useCaisseOperationTypes } from './caisseListPageUtils';
import { QawaemErpModalFormShell } from '../../products/saudi/components/QawaemErpModalFormTabs';
import { useTreasuryConfig, useTreasuryListViewMode } from './useTreasuryConfig';

const INITIAL_CODE_TYPES = SAUDI_CODE_TYPES;

const INITIAL_DEBIT_CATEGORIES = SAUDI_DEBIT_CATEGORIES;

const INITIAL_CREDIT_CATEGORIES = SAUDI_CREDIT_CATEGORIES;

const OperationsHierarchyPage = () => {
  const { t } = useTranslation();
  const treasuryConfig = useTreasuryConfig();
  const [viewMode, setViewMode] = useTreasuryListViewMode(treasuryConfig);
  const { operationCategories } = useCaisseOperationTypes();
  const p = (key, opts) => t(`modules.caisse.pages.operationsHierarchyPage.${key}`, opts);
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityCodeLabel = p('entity_code_label', { defaultValue: 'الرمز' });
  const entityCategoryLabel = p('entity_category_label', { defaultValue: 'الفئة' });
  const entityOperationLabel = p('entity_operation_label', { defaultValue: 'العملية' });
  const [codeFormTab, setCodeFormTab] = useState('basic');
  const codeFormTabDefs = useMemo(() => ([
    { id: 'basic', label: p('section_general'), icon: Tag, iconClassName: 'text-blue-400' },
    { id: 'notes', label: p('subtitle'), icon: FileText, iconClassName: 'text-purple-400' },
  ]), [p]);
  const CODE_FIELD_COUNT = 3;
  const codeFormTabs = useMemo(
    () => buildCaisseModalFormTabs(codeFormTabDefs, CODE_FIELD_COUNT),
    [codeFormTabDefs],
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  const [codeTypes, setCodeTypes] = useState(INITIAL_CODE_TYPES);
  const [codes, setCodes] = useState([]);
  const [activeTab, setActiveTab] = useState('debit');
  const [mainViewMode, setMainViewMode] = useState('all_hierarchy');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const [debitCategories, setDebitCategories] = useState(INITIAL_DEBIT_CATEGORIES);
  const [creditCategories, setCreditCategories] = useState(INITIAL_CREDIT_CATEGORIES);
  
  const [expandedCodeTypes, setExpandedCodeTypes] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  
  const [showAddCodeModal, setShowAddCodeModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showAddOperationModal, setShowAddOperationModal] = useState(false);
  
  const [editingCodeType, setEditingCodeType] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  
  const [newCode, setNewCode] = useState({ id: '', name: '', description: '' });
  const [newCategory, setNewCategory] = useState({ name: '', codeTypeId: '', operations: [] });
  const [newOperation, setNewOperation] = useState({ categoryId: null, value: '' });
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);
  const statusFilterRef = useRef(null);

  useEffect(() => {
    if (!operationCategories?.debit?.length && !operationCategories?.credit?.length) return;
    try {
      localStorage.setItem('operationCategories', JSON.stringify(operationCategories));
    } catch (error) {
      console.error('Failed to sync operation categories:', error);
    }
  }, [operationCategories]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
        setIsDateRangeOpen(false);
      }
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setIsStatusFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    localStorage.removeItem('affectationCodes');

    const SAUDI_HIERARCHY_VERSION = 'sa-v1';
    if (localStorage.getItem('hierarchyVersion') !== SAUDI_HIERARCHY_VERSION) {
      localStorage.removeItem('debitCategories');
      localStorage.removeItem('creditCategories');
      localStorage.removeItem('codeTypes');
      localStorage.setItem('hierarchyVersion', SAUDI_HIERARCHY_VERSION);
    }

    try {
      const savedCodeTypes = localStorage.getItem('codeTypes');
      const savedCodes = localStorage.getItem('affectationCodes');
      const savedDebitCategories = localStorage.getItem('debitCategories');
      const savedCreditCategories = localStorage.getItem('creditCategories');
      
      let loadedCodeTypes = INITIAL_CODE_TYPES;
      if (savedCodeTypes) {
        loadedCodeTypes = JSON.parse(savedCodeTypes);
        setCodeTypes(loadedCodeTypes);
      } else {
        localStorage.setItem('codeTypes', JSON.stringify(INITIAL_CODE_TYPES));
      }
      
      if (savedCodes) {
        setCodes(JSON.parse(savedCodes));
      } else {
        const initialCodes = [];
        setCodes(initialCodes);
        localStorage.setItem('affectationCodes', JSON.stringify(initialCodes));
      }
      
      let loadedDebitCategories = INITIAL_DEBIT_CATEGORIES;
      let loadedCreditCategories = INITIAL_CREDIT_CATEGORIES;
      
      if (savedDebitCategories) {
        loadedDebitCategories = JSON.parse(savedDebitCategories);
        setDebitCategories(loadedDebitCategories);
      } else {
        localStorage.setItem('debitCategories', JSON.stringify(INITIAL_DEBIT_CATEGORIES));
      }
      
      if (savedCreditCategories) {
        loadedCreditCategories = JSON.parse(savedCreditCategories);
        setCreditCategories(loadedCreditCategories);
      } else {
        localStorage.setItem('creditCategories', JSON.stringify(INITIAL_CREDIT_CATEGORIES));
      }
      
      setTimeout(() => {
        syncOperationCategories(loadedDebitCategories, loadedCreditCategories);
      }, 100);
    } catch (error) {
      console.error('Hierarchy data load error:', error);
      toast.error(p('toast_load_error'));
    }
  }, []);

  const saveData = (dataType, newData) => {
    switch (dataType) {
      case 'codeTypes':
        localStorage.setItem('codeTypes', JSON.stringify(newData || codeTypes));
        break;
      case 'codes':
        localStorage.setItem('affectationCodes', JSON.stringify(newData || codes));
        break;
      case 'debitCategories': {
        const debitCategoriesData = newData || debitCategories;
        localStorage.setItem('debitCategories', JSON.stringify(debitCategoriesData));
        syncOperationCategories(debitCategoriesData, creditCategories);
        break;
      }
      case 'creditCategories': {
        const creditCategoriesData = newData || creditCategories;
        localStorage.setItem('creditCategories', JSON.stringify(creditCategoriesData));
        syncOperationCategories(debitCategories, creditCategoriesData);
        break;
      }
      default:
        break;
    }
  };

  const syncOperationCategories = (debitCats, creditCats) => {
    try {
      const savedOperationCategories = localStorage.getItem('operationCategories');
      let operationCategories = savedOperationCategories ? JSON.parse(savedOperationCategories) : { debit: [], credit: [] };
      
      operationCategories.debit = debitCats.map(cat => {
        const codeType = codeTypes.find(ct => ct.id === cat.codeTypeId);
        const categoryName = codeType ? `${codeType.id} - ${codeType.name}` : cat.codeTypeId;
        return { category: categoryName, operations: cat.operations || [] };
      });
      
      operationCategories.credit = creditCats.map(cat => {
        const codeType = codeTypes.find(ct => ct.id === cat.codeTypeId);
        const categoryName = codeType ? `${codeType.id} - ${codeType.name}` : cat.codeTypeId;
        return { category: categoryName, operations: cat.operations || [] };
      });
      
      localStorage.setItem('operationCategories', JSON.stringify(operationCategories));
      localStorage.setItem('debitOperations', JSON.stringify(debitCats));
      localStorage.setItem('creditOperations', JSON.stringify(creditCats));
    } catch (error) {
      console.error('Category sync error:', error);
    }
  };

  const toggleCodeType = (codeTypeId) => {
    setExpandedCodeTypes(prev => ({ ...prev, [codeTypeId]: !prev[codeTypeId] }));
  };

  const toggleCategory = (categoryId, type) => {
    const key = `${type}-${categoryId}`;
    setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getFilteredCategories = (type, codeTypeId) => {
    const categories = type === 'debit' ? debitCategories : creditCategories;
    return categories.filter(cat => 
      cat.codeTypeId === codeTypeId &&
      (searchTerm === '' || 
       cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
       cat.operations.some(op => op.toLowerCase().includes(searchTerm.toLowerCase())))
    );
  };

  const handleSelectOperation = (operation, category, codeType) => {
    toast.success(p('toast_operation_selected', {
      operation,
      category: category.name,
      code: `${codeType.id} - ${codeType.name}`,
    }));
  };

  const handleDeleteCode = (codeTypeId) => {
    if (window.confirm(p('confirm_delete_code'))) {
      const updatedCodes = codes.filter(code => code.codeTypeId !== codeTypeId);
      setCodes(updatedCodes);
      saveData('codes', updatedCodes);
      
      const updatedDebitCategories = debitCategories.filter(cat => cat.codeTypeId !== codeTypeId);
      const updatedCreditCategories = creditCategories.filter(cat => cat.codeTypeId !== codeTypeId);
      
      setDebitCategories(updatedDebitCategories);
      setCreditCategories(updatedCreditCategories);
      
      saveData('debitCategories', updatedDebitCategories);
      saveData('creditCategories', updatedCreditCategories);
      
      notifyDeleted(showNotify, entityCodeLabel);
    }
  };
  
  const handleDeleteCategory = (categoryId, type) => {
    if (window.confirm(p('confirm_delete_category'))) {
      if (type === 'debit') {
        const updatedCategories = debitCategories.filter(cat => cat.id !== categoryId);
        setDebitCategories(updatedCategories);
        saveData('debitCategories', updatedCategories);
      } else {
        const updatedCategories = creditCategories.filter(cat => cat.id !== categoryId);
        setCreditCategories(updatedCategories);
        saveData('creditCategories', updatedCategories);
      }
      
      notifyDeleted(showNotify, entityCategoryLabel);
    }
  };
  
  const handleDeleteOperation = (categoryId, operationIndex, type) => {
    if (window.confirm(p('confirm_delete_operation'))) {
      if (type === 'debit') {
        const updatedCategories = [...debitCategories];
        const categoryIndex = updatedCategories.findIndex(cat => cat.id === categoryId);
        
        if (categoryIndex !== -1) {
          updatedCategories[categoryIndex].operations.splice(operationIndex, 1);
          setDebitCategories(updatedCategories);
          saveData('debitCategories', updatedCategories);
        }
      } else {
        const updatedCategories = [...creditCategories];
        const categoryIndex = updatedCategories.findIndex(cat => cat.id === categoryId);
        
        if (categoryIndex !== -1) {
          updatedCategories[categoryIndex].operations.splice(operationIndex, 1);
          setCreditCategories(updatedCategories);
          saveData('creditCategories', updatedCategories);
        }
      }
      
      notifyDeleted(showNotify, entityOperationLabel);
    }
  };

  const resetAddCodeForm = () => {
    setNewCode({ id: '', name: '', description: '' });
    setCodeFormTab('basic');
    setShowAddCodeModal(false);
  };

  const openAddCodeModal = () => {
    setCodeFormTab('basic');
    setShowAddCodeModal(true);
  };

  const handleAddCodeSubmit = (e) => {
    e.preventDefault();
    const id = String(newCode.id || '').trim().toUpperCase();
    const name = String(newCode.name || '').trim();
    if (!id || !name) {
      toast.error(p('toast_nothing_selected'));
      return;
    }
    if (codeTypes.some((ct) => ct.id === id)) {
      toast.error(p('toast_load_error'));
      return;
    }
    const entry = { id, name, description: String(newCode.description || '').trim() || name };
    const updated = [...codeTypes, entry];
    setCodeTypes(updated);
    saveData('codeTypes', updated);
    notifyCreated(showNotify, entityCodeLabel);
    resetAddCodeForm();
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    // Implementation for select all
  };

  const handleBulkDelete = () => {
    if (selectedRecords.length === 0) {
      toast.error(p('toast_nothing_selected'));
      return;
    }
    
    if (window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) {
      const deleteCount = selectedRecords.length;
      notifyBulkDeleted(showNotify, deleteCount, entityOperationLabel);
      setSelectedRecords([]);
      setSelectAll(false);
    }
  };

  // Calculate stats
  const stats = {
    totalCodes: codeTypes.length,
    totalCategories: debitCategories.length + creditCategories.length,
    totalOperations: debitCategories.reduce((sum, c) => sum + c.operations.length, 0) + creditCategories.reduce((sum, c) => sum + c.operations.length, 0),
    debitOperations: debitCategories.reduce((sum, c) => sum + c.operations.length, 0),
    creditOperations: creditCategories.reduce((sum, c) => sum + c.operations.length, 0)
  };

  const mainTabs = [
    { id: 'all_hierarchy', nameKey: 'tab_all_hierarchy', icon: LayoutGrid },
    { id: 'by_code', nameKey: 'tab_by_code', icon: Tag },
    { id: 'by_type', nameKey: 'tab_by_type', icon: Shield },
    { id: 'by_category', nameKey: 'tab_by_category', icon: Layers }
  ];

  const quickFilterButtons = [
    { id: 'all', nameKey: 'filter_all', icon: Layers },
    { id: 'debit', nameKey: 'filter_debit', icon: TrendingUp },
    { id: 'credit', nameKey: 'filter_credit', icon: CheckCircle },
    { id: 'recent', nameKey: 'filter_recent', icon: Calendar }
  ];

  const getDistributionData = () => {
    return {
      title: p('distribution_title'),
      data: [
        { nameKey: 'dist_codes', count: stats.totalCodes, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { nameKey: 'dist_categories', count: stats.totalCategories, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { nameKey: 'dist_debit_operations', count: stats.debitOperations, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { nameKey: 'dist_credit_operations', count: stats.creditOperations, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    };
  };

  const distributionInfo = getDistributionData();

  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Tag className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={() => window.location.reload()} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          
          <button onClick={openAddCodeModal} className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2">
            <Plus className="h-5 w-5" />
            <span>{p('new_code')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { titleKey: 'stat_total_codes', value: stats.totalCodes, icon: Tag, color: 'from-blue-500 to-blue-600' },
          { titleKey: 'stat_total_categories', value: stats.totalCategories, icon: Layers, color: 'from-purple-500 to-purple-600' },
          { titleKey: 'stat_total_operations', value: stats.totalOperations, icon: Grid, color: 'from-green-500 to-green-600' },
          { titleKey: 'stat_debit', value: stats.debitOperations, icon: TrendingUp, color: 'from-red-500 to-red-600' },
          { titleKey: 'stat_credit', value: stats.creditOperations, icon: CheckCircle, color: 'from-emerald-500 to-emerald-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{p(stat.titleKey)}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(tab.nameKey)}</span>
                </button>
              ))}
            </div>
          </div>
          
          {mainViewMode === 'all_hierarchy' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(filter.nameKey)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <X size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => {
                const label = p(item.nameKey);
                const pct = (() => {
                  const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                  return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                })();
                return (
                <button key={item.nameKey} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.nameKey ? null : item.nameKey)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.nameKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.nameKey ? 'text-white' : 'text-white/80'}`}>{label}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${pct}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.nameKey ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: pct })}</p>
                </button>
              );})}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">

        {/* Tabs */}
        <div className="bg-white/10 backdrop-blur-lg p-2 border-b border-white/10">
          <div className="flex space-x-1">
            <button onClick={() => setActiveTab('debit')} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${activeTab === 'debit' ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
              <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
              <span>{p('tab_debit_operations')}</span>
            </button>
            <button onClick={() => setActiveTab('credit')} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${activeTab === 'credit' ? 'bg-gradient-to-r from-green-600 to-green-500 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
              <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
              <span>{p('tab_credit_operations')}</span>
            </button>
          </div>
        </div>

        {/* Hierarchical Structure */}
        {viewMode === 'hierarchy' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="p-6">
            <div className="space-y-4">
              {codeTypes.map(codeType => {
                const filteredCategories = getFilteredCategories(activeTab, codeType.id);
                if (filteredCategories.length === 0) return null;
                
                return (
                  <motion.div key={codeType.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                    <div className={`p-3 flex items-center justify-between cursor-pointer ${expandedCodeTypes[codeType.id] ? 'bg-white/10' : ''}`} onClick={() => toggleCodeType(codeType.id)}>
                      <div className="flex items-center">
                        {expandedCodeTypes[codeType.id] ? <FolderOpen className="h-5 w-5 text-amber-400 mr-2" /> : <Folder className="h-5 w-5 text-amber-400 mr-2" />}
                        <div>
                          <span className="font-semibold text-white mr-2">{codeType.id} - {codeType.name !== codeType.id ? codeType.name : codeType.id}</span>
                          <span className="text-white/60 text-sm">{codeType.description}</span>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteCode(codeType.id); }} className="p-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors mr-2" title={p('delete_code_title')}><Trash2 className="h-4 w-4" /></button>
                        {expandedCodeTypes[codeType.id] ? <ChevronDown className="h-4 w-4 text-white/60" /> : <ChevronRight className="h-4 w-4 text-white/60" />}
                      </div>
                    </div>
                    
                    {expandedCodeTypes[codeType.id] && (
                      <div className="p-2 pl-6">
                        {filteredCategories.map(category => {
                          const categoryKey = `${activeTab}-${category.id}`;
                          return (
                            <div key={category.id} className="mb-2">
                              <div className={`p-2 flex items-center justify-between cursor-pointer rounded-lg ${expandedCategories[categoryKey] ? 'bg-white/10' : 'hover:bg-white/5'}`} onClick={() => toggleCategory(category.id, activeTab)}>
                                <div className="flex items-center">
                                  {expandedCategories[categoryKey] ? <ChevronDown className="h-4 w-4 text-white/60 mr-2" /> : <ChevronRight className="h-4 w-4 text-white/60 mr-2" />}
                                  <span className="text-white/90 font-medium">{category.name}</span>
                                </div>
                                <div className="flex space-x-2">
                                  <button onClick={(e) => { e.stopPropagation(); handleDeleteCategory(category.id, activeTab); }} className="p-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30" title={p('delete_category_title')}><Trash2 className="h-4 w-4" /></button>
                                </div>
                              </div>
                              
                              {expandedCategories[categoryKey] && (
                                <ul className="mt-1 ml-6 space-y-1">
                                  {category.operations.filter(op => op.toLowerCase().includes(searchTerm.toLowerCase())).map((operation, idx) => (
                                    <li key={idx} className="p-2 rounded-lg flex items-center justify-between hover:bg-white/10 cursor-pointer transition-colors text-white/80 hover:text-white">
                                      <div className="flex items-center" onClick={() => handleSelectOperation(operation, category, codeType)}>
                                        <span className="mr-2">•</span>
                                        {operation}
                                      </div>
                                      <button onClick={(e) => { e.stopPropagation(); handleDeleteOperation(category.id, idx, activeTab); }} className="p-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30" title={p('delete_operation_title')}><Trash2 className="h-4 w-4" /></button>
                                    </li>
                                  ))}
                                  {category.operations.length === 0 && (
                                    <li className="p-2 text-center">
                                      <p className="text-white/50 italic mb-2">{p('empty_category')}</p>
                                    </li>
                                  )}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>
                );
              })}

              {codeTypes.filter(codeType => getFilteredCategories(activeTab, codeType.id).length > 0).length === 0 && (
                <div className="text-center py-10">
                  <p className="text-white/60">{p('no_search_results')}</p>
                  <div className="flex flex-col gap-3 items-center mt-4">
                    <button onClick={openAddCodeModal} className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-2 rounded-xl font-medium hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center">
                      <Plus className="h-5 w-5 mr-2" />
                      {p('add_new_code')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      <QawaemErpCrudModal
        open={showAddCodeModal}
        onClose={resetAddCodeForm}
        title={p('new_code')}
        icon={Tag}
        onSubmit={handleAddCodeSubmit}
        formId="hierarchy-add-code-form"
        saveLabel={p('add_new_code')}
        maxWidth="560px"
        testId="hierarchy-add-code-modal"
      >
        <QawaemErpModalFormShell
          isLight={isLight}
          tabs={codeFormTabs}
          activeTab={codeFormTab}
          onTabChange={setCodeFormTab}
          ariaLabel={p('new_code')}
          formId="hierarchy-add-code-form"
          onSubmit={handleAddCodeSubmit}
        >
          {(codeFormTabs.length === 0 || codeFormTab === 'basic') && (
            <div className={CAISSE_FORM_GRID}>
              <div className="min-w-0">
                <label className={cfgTheme.label}>{p('select')} <span className="qawaem-required-mark">*</span></label>
                <input type="text" required value={newCode.id} onChange={(e) => setNewCode((prev) => ({ ...prev, id: e.target.value.toUpperCase() }))} className={cfgTheme.input} placeholder="CN" />
              </div>
              <div className="min-w-0">
                <label className={cfgTheme.label}>{p('title')} <span className="qawaem-required-mark">*</span></label>
                <input type="text" required value={newCode.name} onChange={(e) => setNewCode((prev) => ({ ...prev, name: e.target.value }))} className={cfgTheme.input} />
              </div>
              {codeFormTabs.length === 0 ? (
                <div className="min-w-0 md:col-span-3">
                  <label className={cfgTheme.label}>{p('subtitle')}</label>
                  <textarea value={newCode.description} onChange={(e) => setNewCode((prev) => ({ ...prev, description: e.target.value }))} className={cfgTheme.input} rows={4} />
                </div>
              ) : null}
            </div>
          )}
          {codeFormTabs.length > 0 && codeFormTab === 'notes' && (
            <div className={CAISSE_FORM_GRID}>
              <div className="min-w-0 md:col-span-3">
                <label className={cfgTheme.label}>{p('subtitle')}</label>
                <textarea value={newCode.description} onChange={(e) => setNewCode((prev) => ({ ...prev, description: e.target.value }))} className={cfgTheme.input} rows={4} />
              </div>
            </div>
          )}
        </QawaemErpModalFormShell>
      </QawaemErpCrudModal>
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default OperationsHierarchyPage;


